import { ArrayItem, SMap } from '@client/new/helpers/types';
import { UpChunk } from '@mux/upchunk';
import {
  GetCreatorsProcessingMediaQuery,
  GetLibraryContentQuery,
} from 'generated/graphql';

export type videoSourceType = 's3' | 'mux';

export type ProcessingVideo = {
  creatorId: number;
  id: string;
  videoSourceType: videoSourceType;
  type: string;

  url: string;
  thumbnailUrl: string;

  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;

  width: number;
  height: number;
  duration: number;
  fileSize: number;
};

export type Video = {
  creatorId: number;
  id: string;
  videoSourceType: videoSourceType;
  type: string;

  url: string;
  downloadUrl?: string;
  thumbnailUrl: string;

  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;

  width: number;
  height: number;
  duration: number;
  fileSize: number;
  folderId: number | null;

  isProcessing?: boolean;
};

export type Folder = {
  id: number;
  name: string;
  parentId: number | null;
  updatedAt: string;
};

export type UploadStatus = 'ready' | 'uploading' | 'finished' | 'error';

export type UploadState = {
  progress: number;
  fileName: string;
  fileId: string;
  state: UploadStatus;
  muxInstance: UpChunk;
  folderId: number | null;
};

export type UploadMap = SMap<UploadState>;

export type GQLVideo = ArrayItem<GetLibraryContentQuery['getCreatorsMedia']>;
export type GQLFolder = ArrayItem<GetLibraryContentQuery['getCreatorsFolders']>;
export type Videos = Array<Video>;
export type Folders = Array<Folder>;

export type GQLProcessingVideo = ArrayItem<
  GetCreatorsProcessingMediaQuery['getCreatorsProcessingMedia']
>;
export type ProcessingVideos = Array<ProcessingVideo>;

const toDownloadUrl = (download: GQLVideo['downloadLink']) =>
  download
    ? download?.high?.link || download?.medium?.link || download?.low?.link
    : undefined;

export const toVideo = (v: GQLVideo): Video => ({
  id: v.uuid,
  creatorId: v.creatorId,
  videoSourceType: v.videoSourceType as videoSourceType,
  type: v.type,

  thumbnailUrl: v.videoThumbnailUrl,
  url: v.videoUrl,
  downloadUrl: toDownloadUrl(v.downloadLink),

  name: v.title,
  description: v.description || '',
  createdAt: v.createdAt,
  updatedAt: v.updatedAt,

  width: v.width,
  height: v.height,
  fileSize: v.fileSize || 0,
  duration: v.duration || 0,

  folderId: v?.folderId || null,
  isProcessing: false,
});

export const toFolder = (f: GQLFolder): Folder => ({
  id: f.id,
  name: f.name,
  parentId: f.parentId || null,
  updatedAt: f.updatedAt,
});

export const minFileSizeWarning = 1024 * 1024 * 100;
